import { Alert, Button, Card, Flex, Heading, SelectField, TextAreaField, TextField, useTheme, View } from "@aws-amplify/ui-react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import './ReportProblemOverlay.css';
import { REPORT_CATEGORY_EMAILS_ENUM, SENDER_EMAIL_CATEGORY_ENUM } from "../utils/ReportProblemConfig";
import { EmailUtils } from "../utils/EmailUtils";
import InputSanitizationUtils from "../utils/InputSanitizationUtils";

interface ReportProblemOverlayProps {
    handleCloseOverlay: () => void;
}

export default function ReportProblemOverlay({ handleCloseOverlay }: ReportProblemOverlayProps) {
    const { t } = useTranslation();
    const { user } = useAuthenticator(context => [context.user]);
    const { tokens } = useTheme();

    const [topic, setTopic] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [body, setBody] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleSubmit = async () => {
        if (!topic.trim() || !subject.trim() || !body.trim()) {
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        // Sanitize all user inputs before sending email
        const sanitizedSubject = InputSanitizationUtils.sanitizePlainText(subject);
        const sanitizedBody = InputSanitizationUtils.sanitizePlainText(body);

        try {
            const result = await EmailUtils.sendEmail({
                senderCategory: SENDER_EMAIL_CATEGORY_ENUM.REPORT_FORM.id,
                topic,
                subject: sanitizedSubject,
                body: sanitizedBody,
            });

            if (result.success) {
                setShowSuccess(true);
                // Close overlay after a short delay
                setTimeout(() => {
                    handleCloseOverlay();
                }, 3000);
            } else {
                setErrorMessage(result.error || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            setErrorMessage('An unexpected error occurred while sending the email');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            handleCloseOverlay();
        }
    };

    // Add event listener for escape key
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    if (!user) {
        return (
            <View className="overlay-content report-problem-overlay"
                backgroundColor={tokens.colors.background.secondary}
                padding={tokens.space.medium}
            >
                <Card>
                    <Button
                        className="report-problem-close-button"
                        variation="link"
                        size="large"
                        onClick={handleCloseOverlay}
                    >
                        &times;
                    </Button>
                    <Flex direction="column" alignItems="center" gap={tokens.space.medium}>
                        <Heading level={3}>
                            {t('report-problem.login-required')}
                        </Heading>
                        <p>{t('report-problem.login-to-report')}</p>
                    </Flex>
                </Card>
            </View>
        );
    }

    return (
        <View className="overlay-content report-problem-overlay"
            backgroundColor={tokens.colors.background.secondary}
            padding={tokens.space.medium}
        >
            <Card>
                <Button
                    className="report-problem-close-button"
                    variation="link"
                    size="large"
                    onClick={handleCloseOverlay}
                >
                    &times;
                </Button>
                
                <Flex direction="column" gap={tokens.space.medium}>
                    <Heading level={3}>
                        {t('report-problem.title')}
                    </Heading>
                    
                    <Alert variation="info" hasIcon>
                        {t('report-problem.info-message')}
                    </Alert>

                    <SelectField
                        label={t('report-problem.topic-label')}
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder={t('report-problem.topic-placeholder')}
                        required
                    >
                        {Object.values(REPORT_CATEGORY_EMAILS_ENUM)
                            .sort((a, b) => a.order - b.order)
                            .map((category) => (
                                <option key={category.id} value={category.id}>
                                    {t(`report-problem.topics.${category.id}`)}
                                </option>
                            ))}
                    </SelectField>

                    <TextField
                        label={t('report-problem.subject-label')}
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t('report-problem.subject-placeholder')}
                        required
                    />

                    <TextAreaField
                        label={t('report-problem.body-label')}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={t('report-problem.body-placeholder')}
                        rows={6}
                        required
                    />

                    {errorMessage && (
                        <Alert variation="error" hasIcon>
                            {errorMessage}
                        </Alert>
                    )}

                    {showSuccess && (
                        <Alert variation="success" hasIcon>
                            {t('report-problem.success-message')}
                        </Alert>
                    )}

                    <Flex gap={tokens.space.small} justifyContent="flex-end">
                        <Button
                            variation="link"
                            onClick={handleCloseOverlay}
                        >
                            {t('report-problem.cancel')}
                        </Button>
                        <Button
                            variation="primary"
                            onClick={handleSubmit}
                            isLoading={isSubmitting}
                            isDisabled={!topic.trim() || !subject.trim() || !body.trim()}
                        >
                            {t('report-problem.send')}
                        </Button>
                    </Flex>
                </Flex>
            </Card>
        </View>
    );
} 